import React, { useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import Navbar from '../Navbar/Navbar'
import Sidebar from '../Sidebar/Sidebar'
import authService from '@/services/authService'
import navigationConfig from '@config/navigation'
import { ROLES } from '@config/roles'
import '../DashboardLayout/DashboardLayout.scss'

/**
 * Role-Based Layout Component
 *
 * Dynamically renders the appropriate navigation based on user role.
 * This replaces the static role prop in DashboardLayout with dynamic role detection.
 *
 * Uses authService to get current user's role and renders appropriate navigation.
 */
const RoleBasedLayout = () => {
	const navigate = useNavigate()
	const [sidebarOpen, setSidebarOpen] = useState(true)
	const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

	// Get current user from auth service
	const user = authService.getCurrentUser()

	/**
	 * Map backend role slug to frontend ROLES constant
	 */
	const getRoleFromUser = (userData) => {
		if (!userData || !userData.role) {
			return ROLES.EMPLOYEE
		}

		// Handle both role object (from backend) and role string
		const roleSlug = typeof userData.role === 'object'
			? userData.role.slug
			: userData.role

		// Map backend role slugs to frontend ROLES constants
		const roleMapping = {
			'super-admin': ROLES.SUPER_ADMIN,
			'superadmin': ROLES.SUPER_ADMIN,
			'product-owner': ROLES.SUPER_ADMIN,
			'company-admin': ROLES.COMPANY_ADMIN,
			'compliance-manager': ROLES.COMPANY_ADMIN,
			'project-manager': ROLES.PROJECT_MANAGER,
			'management': ROLES.MANAGEMENT,
			'qa': ROLES.MANAGEMENT,
			'executive': ROLES.MANAGEMENT,
			'employee': ROLES.EMPLOYEE,
			'team-member': ROLES.EMPLOYEE
		}

		return roleMapping[roleSlug?.toLowerCase()] || ROLES.EMPLOYEE
	}

	const userRole = getRoleFromUser(user)

	// Get navigation items for the user's role
	const navItems = navigationConfig[userRole] || []

	const handleLogout = async () => {
		try {
			await authService.logout()
			navigate('/login', { replace: true })
		} catch (error) {
			console.error('Logout error:', error)
			// Force logout on client side even if API fails
			authService.clearAuth()
			navigate('/login', { replace: true })
		}
	}

	const toggleSidebar = () => {
		setSidebarOpen(!sidebarOpen)
	}

	const toggleSidebarCollapse = () => {
		setSidebarCollapsed(!sidebarCollapsed)
	}

	return (
		<div className={`dashboard-wrapper ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
			{/* Decorative Borders */}
			<div className="decorative-border decorative-border--top" />
			<div className="decorative-border decorative-border--bottom" />
			<div className="decorative-border decorative-border--left" />
			<div className="decorative-border decorative-border--right" />

			{/* Navbar */}
			<Navbar
				onToggleSidebar={toggleSidebar}
				onToggleSidebarCollapse={toggleSidebarCollapse}
				sidebarCollapsed={sidebarCollapsed}
				onLogout={handleLogout}
			/>

			{/* Sidebar */}
			<Sidebar
				isOpen={sidebarOpen}
				isCollapsed={sidebarCollapsed}
				onClose={() => setSidebarOpen(false)}
				navItems={navItems}
			/>

			{/* Main Content - Outlet renders child routes */}
			<main className="main-content">
				<div className="container-fluid">
					<Outlet />
				</div>
			</main>

			{/* Overlay for mobile */}
			{sidebarOpen && (
				<div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
			)}
		</div>
	)
}

export default RoleBasedLayout
